import {isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {CampaignData, FormData, FormType} from '@dcp/configurator';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Product} from '@spartacus/core';
import {DcpProductUtilitiesService} from '../../product/services/dcp-product-utilities.service';
import {FormUuidService} from './form-uuid.service';

declare const _trackData: (object: object) => void | undefined;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class FormDataTrackingService {
  private featureIsEnabled: boolean = false;

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    private formUuidService: FormUuidService,
    private productUtilitiesService: DcpProductUtilitiesService,
  ) {
  }

  /**
   * Tracks a form view action and send information to adobe analytics.
   *
   * @param name of the form, can be any string
   * @param country of the form, mainly for marketing purpose, can be empty
   * @param product that is currently displayed and the form is used for
   */
  public trackFormView(name: string, country?: string, product?: Product): void {
    try {
      if (
        this.featureIsEnabled &&
        isPlatformBrowser(this.platformId) &&
        window &&
        _trackData
      ) {
        const data: FormData = new FormData();
        data.events = {formView: true};
        data.form = {
          uniqueID: this.formUuidService.getTrimmedUuidByFormName(name),
          name,
          referrer: window.location.href,
        };
        this.populateProductData(product, data);
        _trackData(data);
      }
    } catch (e) {}
  }

  /**
   * Tracks a successful form submit action and send information to adobe analytics.
   *
   * @param transactionId that references the lead
   * @param name of the form, can be any string
   * @param country of the form, mainly for marketing purpose, can be empty
   * @param campaignData from url parameters
   * @param product that is currently displayed and the form is used for
   */
  public trackFormSubmit(
    transactionId: string,
    name: string,
    country?: string,
    campaignData?: CampaignData,
    product?: Product
  ): void {
    try {
      if (this.featureIsEnabled && isPlatformBrowser(this.platformId) && _trackData) {
        const data: FormData = new FormData();
        data.events = {formSubmit: true};
        data.form = {
          uniqueID: this.formUuidService.getTrimmedUuidByFormName(name),
          transactionID: transactionId,
          name,
          country,
          campaign_data: campaignData,
        };
        this.populateProductData(product, data);
        _trackData(data);
        this.formUuidService.getTrimmedUuidByFormName(name, undefined, true);
      }
    } catch (e) {}
  }

  /**
   * Tracks an error that occurred during a form submit.
   *
   * @param errorMessage that describes the error, can be any string
   * @param product optional current product
   */
  public trackFormError(errorMessage: string, product?: Product): void {
    try {
      if (this.featureIsEnabled && isPlatformBrowser(this.platformId) && _trackData) {
        const data: FormData = new FormData();
        data.events = {error: true};
        data.error = {
          description: errorMessage,
        };
        this.populateProductData(product, data);
        _trackData(data);
      }
    } catch (e) {}
  }

  /**
   * Generates form names according to the adobe analytics requirements.
   * <PUBLISHER>_<COUNTRY>_<YEAR>_<MONTH>_PME_<TYPE>_<NAME_UNIQUE>
   *
   * @param formType defines the purpose of the form
   * @param product the form is about
   * @param country of the form, mainly for marketing purpose, can be empty
   */
  public generateFormName(formType: FormType, product: Product, country: string = 'G'): string {
    const currentDate: Date = new Date();
    const currentYear: string = currentDate.getFullYear().toString(10);
    const currentMonth: string = ('0' + (currentDate.getMonth() + 1)).slice(-2);
    const name: string = `${this.productUtilitiesService
      .getProductId(product)
      .replace(/\s/g, '_')}_${product?.name.replace(/\s/g, '_')}`;
    return `sapapp_${country}_${currentYear}_${currentMonth}_PME_${formType || 'CT'}_${name}`;
  }

  /**
   * Populates the product section of the form Data with data from the given product.
   *
   * @param product to get the data from
   * @param data object for the form data tracking
   */
  protected populateProductData(product: Product, data: FormData): void {
    if (data && product) {
      data.ecommerce = {
        products: [
          {
            ID: this.productUtilitiesService.getProductId(product),
            name: product.name,
            partnerName: product.vendor ? product.vendor.name : undefined,
          },
        ],
      };
    }
  }
}
